import { Typography, Button, Stack, Badge } from '@mui/material';
import React, { forwardRef, RefObject, useEffect, useState } from 'react';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { useSelector } from 'react-redux';
import { selectMindItem } from '../../../../reducers/mindItems.selectors';
import { useReaderContext } from '../../../Reader/ReaderContext';
import { useTranslation } from 'react-i18next';
import { AIAssistantPageSelectionDialog } from '../../shared/Chat/Messages/AIAssistantPageSelectionDialog';
import { formatNumberRanges } from '../../shared/utils';
import config from 'config';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';

const maxDocumentPagesCount = config.ai.readerChat.maxDocumentPagesCount;

export const enum PageSelectionMode {
  DEFAULT = 'default',
  CUSTOM = 'custom',
}

interface AIAssistantPageSelectorProps {
  handleHintClose?: () => void;
}

export const AIAssistantPageSelector = forwardRef((props: AIAssistantPageSelectorProps, ref) => {
  const { t } = useTranslation();
  const { currentPage: originalCurrentPage,
    mindItemId,
    setSelectedPagesForAI,
    selectedPagesForAI,
  } = useReaderContext();
  const currentPage = originalCurrentPage - 1;
  const mindItem = useSelector(state => selectMindItem(state, mindItemId) as MindItem<'document'>);
  const [isCustomPageSelectionDialogOpen, setIsCustomPageSelectionDialogOpen] = useState(false);
  const [pageSelectionMode, setPageSelectionMode] = useState<PageSelectionMode>(PageSelectionMode.DEFAULT);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);

  useEffect(() => {
    if (pageSelectionMode === PageSelectionMode.DEFAULT) {
      setSelectedPagesForAI([currentPage]);
    }
  }, [pageSelectionMode, currentPage, setSelectedPagesForAI]);

  const resolveMindItemContextDescription = () => {
    if (pageSelectionMode === PageSelectionMode.DEFAULT) {
      return t('Current page') + ' ' + (originalCurrentPage);
    } else {
      // +1 because the page numbers are 1-based
      return `{${formatNumberRanges(selectedPagesForAI.map((p: number) => p + 1))}}`;
    }
  };

  const handlePageClick = (page: number) => {
    if (selectedPages.includes(page)) {
      setSelectedPages(selectedPages.filter(p => p !== page));
    } else {
      if (selectedPages.length >= maxDocumentPagesCount) {
        return;
      }
      const set = new Set([...selectedPages, page]);
      setSelectedPages(Array.from(set));
    }
  };

  const handleSelectAllPages = (selected: boolean) => {
    if (selected) {
      setSelectedPages(Array.from({ length: Math.min(maxDocumentPagesCount, mindItem.pageCount) }, (_, i) => i));
    } else {
      setSelectedPages([]);
    }
  };

  const handleSubmit = () => {
    if (selectedPages.length > 0) { 
      setPageSelectionMode(PageSelectionMode.CUSTOM);
      setSelectedPagesForAI(selectedPages);
    } else {
      setSelectedPagesForAI([currentPage]);
      setPageSelectionMode(PageSelectionMode.DEFAULT);
    }
    setIsCustomPageSelectionDialogOpen(false);
  };

  const handleReset = () => {
    setSelectedPages([]);
  };

  const handleClose = () => {
    setIsCustomPageSelectionDialogOpen(false);
    if (pageSelectionMode === PageSelectionMode.CUSTOM) {
      setSelectedPages(selectedPagesForAI);
    } else {
      setSelectedPages([]);
    }
  };

  const truncateSx = getTextTruncationSx(1);

  const handleButtonClick = () => {
    props.handleHintClose?.();
    setIsCustomPageSelectionDialogOpen(true);
  };
  
  return (
    <>
      <Stack gap={spaces.xxs} sx={{ paddingBottom: spaces.xs }} ref={ref as RefObject<HTMLDivElement>}>
        <Stack 
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ width: '100%' }}
        >
          <Typography
            variant='overline'>
            {t('Pages for AI analysis')}
          </Typography>
          <Badge
            variant='dot'
            color='primary'
            invisible={pageSelectionMode === PageSelectionMode.DEFAULT}
            sx={{
              '& .MuiBadge-dot': {
                right: 3,
                top: 3,
              },
            }}
          >
            <Button
              variant='outlined'
              color='primary'
              onClick={handleButtonClick}
              size='small'
            >
              {t('Select pages')}
            </Button>
          </Badge>
        </Stack>
        <Typography 
          variant='caption' 
          sx={{ 
            color: palette.text.secondary,
            ...truncateSx,
          }}
          data-testid='page-selection-description'
        >
          {resolveMindItemContextDescription()}
        </Typography>
      </Stack>

      <AIAssistantPageSelectionDialog
        open={isCustomPageSelectionDialogOpen}
        handleClose={handleClose}
        mindItem={mindItem}
        selectedPages={selectedPages}
        handlePageClick={handlePageClick}
        handleSelectAllPages={handleSelectAllPages}
        handleSubmit={handleSubmit}
        handleReset={handleReset}
        pageSelectionMode={pageSelectionMode}
      />
    </>
  );
});

